import { Autocomplete as MUIAutocomplete, SxProps, createFilterOptions } from '@mui/material'
import { FieldProps } from 'formik'
import * as React from 'react'
import { VinciInput } from '~/components'

const filter = createFilterOptions<unknown>()

export interface IVinciAutocompleteProps extends FieldProps {
  disabled?: boolean
  freeSolo?: boolean
  label: string
  primaryIdentifier: string
  optionLabelGetter?: (item: any) => string
  options?: any[]
  onSelect: (props: any) => void
  onCreate?: (value: string) => void
  sx?: SxProps
  error?: boolean
  helperText?: string | false | undefined | null
  handleBlur?: (value: string) => void
  uniqueKey?: string
  defaultValue?: T
  customValue?: string
}

type HandleChangeProps =
  | {
      inputValue?: string
    }
  | string

export const VinciAutocomplete: React.FC<
  Omit<Partial<React.ComponentProps<typeof MUIAutocomplete>>, keyof IVinciAutocompleteProps> &
    IVinciAutocompleteProps
> = ({
  disabled,
  field,
  form: { setFieldValue },
  freeSolo = true,
  primaryIdentifier,
  optionLabelGetter,
  options = [],
  label,
  onSelect,
  onCreate,
  sx,
  error,
  helperText,
  handleBlur,
  uniqueKey,
  defaultValue = '',
  customValue = '',
  ...rootProps
}) => {
  const handleChange = (selectedValue: HandleChangeProps) => {
    if (typeof selectedValue === 'string' || Boolean(selectedValue?.inputValue)) {
      const newValue = typeof selectedValue === 'string' ? selectedValue : selectedValue.inputValue
      if (typeof onCreate === 'function') {
        onCreate(newValue || '')
      }
      return
    }
    onSelect(selectedValue)
    setFieldValue(field.name, selectedValue)
  }

  const defaultRenderOption = React.useCallback(
    (props: object, option: any) => (
      <li {...props} {...(uniqueKey ? { key: uniqueKey } : {})}>
        {optionLabelGetter && !option.inputValue
          ? optionLabelGetter(option)
          : option[primaryIdentifier]}
      </li>
    ),
    [optionLabelGetter, primaryIdentifier, uniqueKey],
  )

  return (
    <MUIAutocomplete
      {...rootProps}
      value={field.value ?? customValue}
      disabled={disabled}
      freeSolo={freeSolo}
      onChange={(event, newValue) => handleChange(newValue)}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleBlur?.(e.target.value)}
      options={options}
      defaultValue={defaultValue}
      getOptionLabel={(option) => {
        if (!option) return ''
        if (optionLabelGetter && !option.inputValue) return optionLabelGetter(option)

        if (typeof option === 'string') {
          return option
        }
        if (option.inputValue) {
          return option.inputValue
        }
        return option[primaryIdentifier]
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params)

        if (params.inputValue !== '' && typeof onCreate === 'function') {
          filtered.push({
            inputValue: params.inputValue,
            createOption: true,
            [primaryIdentifier]: `Create "${params.inputValue}"`,
          })
        }

        return filtered
      }}
      renderOption={(props, option, state) => {
        if (option.createOption === true) return defaultRenderOption(props, option)

        return rootProps.renderOption
          ? rootProps.renderOption(props, option, state)
          : defaultRenderOption(props, option)
      }}
      renderInput={(params) => (
        <VinciInput label={label} {...params} error={error} helperText={helperText} />
      )}
      sx={sx}
    />
  )
}
